import React from 'react'
import {
  useCreateMutation,
  useUpdateUserOrganizationRoleMutation,
  useUpdateWorkerMutation,
} from '../../../../queries/users'
import useStore from '../../../../store'
import { UpdateWorkerPayload, User, UserRoleKind } from '../../../../api/users'
import { Button, Columns, Container, Element, Heading, Message } from 'react-bulma-components'
import { Field, FieldArray, Form, Formik } from 'formik'
import InputField from '../../../form/fields/input'
import { OrganizationTypeEnum } from '../../../../api/organizations'
import { RequestButton, RequestMessage } from '../../../request-components/request-components'
import {
  getUserRole,
  getWorkerAndOrganizationAssociation,
  getActiveContractualSchedule,
} from '../../../../utils/users'
import { CreateUserParams } from '../../../../../../backend/src/services/resources/users/users.controller.service.types'
import SelectField from '../../../form/fields/select'
import usersLocales from '../../../../../../locales/users'
import Modal, { useModal } from '../../../modal'
import { MassImport } from '../../../../pages/users/mass-import'
import Protected, { isAuthorizedFor } from '../../../protected/protected'
import ComponentFooter from '../../../sections/component-footer'
import { UserEmployerMemberRole } from '../../../../../../backend/src/services/resources/users/user.model'
import WorkerLabel from '../../../texts/worker-label'
import { useOrganizationsQuery } from '../../../../queries/organizations'
import CheckboxField from '../../../form/fields/checkbox'
import {
  ContractualScheduleEntryFormValue,
  transformContractualSchedulesFromAPI,
  transformContractualSchedulesToAPI,
  createNewContractualScheduleEntry,
  hasWorkerEverHadTimeSlots,
} from './contractual-schedules-utils'
import { validateContractualSchedules } from './contractual-schedules-validation'
import ContractualSchedulesSection from './contractual-schedules-section'

interface UserFormProps {
  user?: User
  role: UserRoleKind
  noAppAccess?: boolean
}

interface UserFormValue {
  email: string
  firstName: string
  lastName: string
  internalRef?: string
  internalInformation?: string
  internalComment?: string
  organizationRole?: 'admin' | UserEmployerMemberRole
  schedule: {
    weeklyHoursWorked: number
    monthlyHoursWorked: number
  }
  laborCostCoefficients?: {
    [key: string]: { isManagement: boolean }
  }
  contractualSchedules?: ContractualScheduleEntryFormValue[]
}

export type InterimAgencyWorkerFormValue = Pick<
  UserFormValue,
  | 'email'
  | 'firstName'
  | 'lastName'
  | 'internalRef'
  | 'internalComment'
  | 'internalInformation'
  | 'laborCostCoefficients'
>
export type EmployerWorkerFormValue = Pick<
  UserFormValue,
  | 'email'
  | 'firstName'
  | 'lastName'
  | 'internalRef'
  | 'schedule'
  | 'internalComment'
  | 'internalInformation'
>

// Re-export for convenience
export type { UserFormValue }

interface UserFormValues {
  newUsers: Array<UserFormValue>
}

interface UserFormErrors {
  newUsers: {
    [key: number]: Partial<Record<keyof UserFormValues['newUsers'][number], string>>
  }
}

const UserForm: React.FC<UserFormProps> = ({ user, role, noAppAccess }) => {
  const createMutation = useCreateMutation()
  const updateMutation = useUpdateWorkerMutation()
  const updateUserOrganizationRole = useUpdateUserOrganizationRoleMutation()
  const currentOrganization = useStore(state => state.session.currentOrganization)
  const userAssociation =
    user && currentOrganization && getWorkerAndOrganizationAssociation(user, currentOrganization)
  const userRole =
    user && currentOrganization && role === 'employerMember'
      ? getUserRole(user, role, currentOrganization._id)
      : undefined
  const importCsvModal = useModal({})

  const associatedEmployerQuery = useOrganizationsQuery(
    { type: 'employer' },
    { enabled: isAuthorizedFor(['interimAgencyMember']) },
  )

  /* TODO: Review this in v5*/
  const description = useDescription(role, currentOrganization?.type)

  const initalLaborCostCoefficients: UserFormValue['laborCostCoefficients'] =
    userAssociation?.laborCostCoefficients.reduce<UserFormValue['laborCostCoefficients']>(
      (formValue, coef) => {
        if (!formValue) formValue = {}
        formValue[coef.employer.toString()] = {
          isManagement: coef.kind === 'management',
        }
        return formValue
      },
      {},
    )

  const initialContractualSchedules: UserFormValue['contractualSchedules'] =
    userAssociation?.contractualSchedules
      ? transformContractualSchedulesFromAPI(userAssociation.contractualSchedules)
      : // For new workers (creation), initialize with empty schedule for Monday to Friday
        role === 'worker' && !user && currentOrganization?.type === OrganizationTypeEnum.employer
        ? [createNewContractualScheduleEntry()]
        : undefined

  // Get active contractual schedule entry to initialize schedule for compatibility
  const activeContractualSchedule = userAssociation
    ? getActiveContractualSchedule(userAssociation)
    : undefined

  const emailCanBeUpdated =
    (!user || (user && !user.isCompleted && !user.associations[1])) && !noAppAccess

  return (
    <Formik<UserFormValues>
      initialValues={{
        newUsers: [
          {
            email: user?.email && emailCanBeUpdated ? user?.email : '',
            firstName: user?.firstName || '',
            lastName: user?.lastName || '',
            internalRef: userAssociation?.internalRef,
            internalInformation: userAssociation?.internalInformation,
            internalComment: userAssociation?.internalComment,
            organizationRole: userRole && userRole.organizationRole,
            schedule: {
              weeklyHoursWorked: activeContractualSchedule?.weeklyHoursWorked || 0,
              monthlyHoursWorked: activeContractualSchedule?.monthlyHoursWorked || 0,
            },
            laborCostCoefficients: initalLaborCostCoefficients,
            contractualSchedules: initialContractualSchedules,
          },
        ],
      }}
      enableReinitialize
      validate={values => {
        const errors: UserFormErrors = {
          newUsers: {},
        }
        let index = 0
        for (const newUser of values.newUsers) {
          if (emailCanBeUpdated && !newUser.email) {
            errors.newUsers[index] = { ...errors.newUsers[index], email: 'Champ requis' }
          }
          if (role === 'worker' && !user) {
            if (!newUser.firstName) {
              errors.newUsers[index] = {
                ...errors.newUsers[index],
                firstName: 'Champ requis',
              }
            }
            if (!newUser.lastName) {
              errors.newUsers[index] = { ...errors.newUsers[index], lastName: 'Champ requis' }
            }
          } else if (role === 'worker' && user) {
            if (!newUser.internalRef) {
              errors.newUsers[index] = {
                ...errors.newUsers[index],
                internalRef: 'Champ requis',
              }
            }
          }
          if (role === 'employerMember') {
            if (!newUser.organizationRole) {
              errors.newUsers[index] = {
                ...errors.newUsers[index],
                organizationRole: 'Champ requis',
              }
            }
          }
          // Validate contractual schedules if they exist
          if (
            role === 'worker' &&
            currentOrganization?.type === OrganizationTypeEnum.employer &&
            newUser.contractualSchedules
          ) {
            const contractualSchedulesErrors = validateContractualSchedules(
              newUser.contractualSchedules,
            )
            // Merge validation errors into form errors
            Object.keys(contractualSchedulesErrors).forEach(errorKey => {
              const errorMessage = contractualSchedulesErrors[errorKey]
              if (errorMessage) {
                // Convert error key from "contractualSchedules.X.Y" to "newUsers.0.contractualSchedules.X.Y"
                const fullErrorKey = `newUsers.${index}.${errorKey}`
                if (!errors.newUsers[index]) {
                  errors.newUsers[index] = {}
                }
                // Formik stores errors as flat keys (e.g., "newUsers.0.contractualSchedules.0.schedule.monday.timeSlots.0.startHour")
                // We need to set the error at the full path as a flat key
                ;(errors as any)[fullErrorKey] = errorMessage
              }
            })
          }
          index++
        }
        console.log('errors', errors)
        if (Object.keys(errors.newUsers).length > 0) {
          return errors
        }
        return {}
      }}
      onSubmit={async values => {
        const { newUsers } = values
        for (const newUser of newUsers) {
          const {
            email,
            firstName,
            lastName,
            schedule,
            internalRef,
            internalComment,
            internalInformation,
            organizationRole,
            laborCostCoefficients,
            contractualSchedules,
          } = newUser

          if (!user) {
            const payload: CreateUserParams = {
              firstName,
              lastName,
              role: { kind: role },
              appAccess: noAppAccess ? 'none' : 'full',
            }
            if (role === 'worker' && currentOrganization) {
              payload.association = currentOrganization._id
              payload.internalRef = internalRef
              payload.internalComment = internalComment
              payload.internalInformation = internalInformation
              if (currentOrganization.type === OrganizationTypeEnum.employer) {
                // Create contractualSchedules entry from schedule or use existing contractualSchedules
                if (contractualSchedules && contractualSchedules.length > 0) {
                  payload.contractualSchedules =
                    transformContractualSchedulesToAPI(contractualSchedules)
                } else if (schedule.weeklyHoursWorked || schedule.monthlyHoursWorked) {
                  // Create a new entry from schedule values if contractualSchedules is empty
                  const newEntry = createNewContractualScheduleEntry(
                    new Date(),
                    schedule.weeklyHoursWorked,
                    schedule.monthlyHoursWorked,
                  )
                  payload.contractualSchedules = transformContractualSchedulesToAPI([newEntry])
                }
              }
            }
            if (!noAppAccess) payload.email = email
            if (
              (role === 'interimAgencyMember' || role === 'employerMember') &&
              currentOrganization
            ) {
              payload.role.organization = currentOrganization._id
              payload.role.organizationRole = organizationRole || 'admin'
            }

            await createMutation.mutateAsync(payload).then(
              res =>
                new Promise(resolve => {
                  setTimeout(() => {
                    resolve(res)
                  }, 250)
                }),
            )
          } else if (role === 'employerMember') {
            if (organizationRole) {
              updateUserOrganizationRole.mutate({
                userId: user._id,
                organizationRole,
              })
            }
          } else {
            const payload: UpdateWorkerPayload = {
              id: user._id,
            }
            if (role === 'worker') {
              payload.internalRef = internalRef
              payload.internalComment = internalComment
              payload.internalInformation = internalInformation
              payload.firstName = firstName
              payload.lastName = lastName
              if (emailCanBeUpdated) payload.email = email
              if (
                currentOrganization?.type === OrganizationTypeEnum.interimAgency &&
                laborCostCoefficients
              ) {
                payload.laborCostCoefficients = Object.keys(laborCostCoefficients).map(
                  employerId => ({
                    employer: employerId,
                    kind: laborCostCoefficients[employerId]?.isManagement
                      ? 'management'
                      : 'delegation',
                  }),
                )
              }
            }
            if (currentOrganization?.type === OrganizationTypeEnum.employer) {
              // Use contractualSchedules if they exist, otherwise create from schedule
              if (contractualSchedules && contractualSchedules.length > 0) {
                payload.contractualSchedules =
                  transformContractualSchedulesToAPI(contractualSchedules)
              } else if (schedule.weeklyHoursWorked || schedule.monthlyHoursWorked) {
                // Create a new entry from schedule values if contractualSchedules is empty
                const newEntry = createNewContractualScheduleEntry(
                  new Date(),
                  schedule.weeklyHoursWorked,
                  schedule.monthlyHoursWorked,
                )
                payload.contractualSchedules = transformContractualSchedulesToAPI([newEntry])
              }
            }

            updateMutation.mutate(payload)
          }
        }
      }}
    >
      {({ values, setFieldValue, isSubmitting }) => {
        return (
          <Form>
            <Container>
              <Element mb={5}>{!user && !noAppAccess && description}</Element>
              <Modal
                title="Importer depuis un fichier CSV"
                actions={importCsvModal}
                maxWidth={1080}
              >
                <MassImport
                  onSuccess={newUsers => {
                    setFieldValue('newUsers', newUsers)
                    importCsvModal.setIsDisplayed(false)
                  }}
                  model={
                    currentOrganization?.type === 'interimAgency'
                      ? [
                          'email',
                          'firstName',
                          'lastName',
                          'internalRef',
                          'internalComment',
                          'internalInformation',
                        ]
                      : [
                          'email',
                          'firstName',
                          'lastName',
                          'internalRef',
                          'schedule.weeklyHoursWorked',
                          'schedule.monthlyHoursWorked',
                          'internalComment',
                          'internalInformation',
                        ]
                  }
                />
              </Modal>
              {isSubmitting && (
                <Modal
                  title="Invitation en cours..."
                  actions={{
                    isDisplayed: true,
                    setIsDisplayed: () => {
                      return
                    },
                  }}
                >
                  Invitation en cours : {createMutation.data?.data.email}
                </Modal>
              )}
              {values.newUsers.length > 1 ? (
                <Message color="info">
                  <Message.Header>
                    {values.newUsers.length} invitations sont prêtes à être envoyées !
                  </Message.Header>
                  <Message.Body>Ne fermez pas votre navigateur durant l'opération</Message.Body>
                </Message>
              ) : (
                <FieldArray name="newUsers">
                  {() => {
                    return values.newUsers.map((newUser, index) => {
                      return (
                        <Element key={index}>
                          {emailCanBeUpdated && (
                            <Field
                              name={`newUsers.${index}.email`}
                              label="Email"
                              type="email"
                              component={InputField}
                              required
                              placeholder="adresse@email.com"
                            />
                          )}
                          {role === 'employerMember' && (
                            <Field
                              label="Rôle"
                              name={`newUsers.${index}.organizationRole`}
                              component={SelectField}
                              options={[
                                { value: 'admin', label: usersLocales.organizationRoles['admin'] },
                                {
                                  value: 'teamLeader',
                                  label: usersLocales.organizationRoles['teamLeader'],
                                },
                                {
                                  value: 'clocker',
                                  label: usersLocales.organizationRoles['clocker'],
                                },
                              ]}
                            />
                          )}
                          {role === 'worker' && (
                            <Columns>
                              <Columns.Column size={6}>
                                <Field
                                  label="Nom de Naissance"
                                  name={`newUsers.${index}.lastName`}
                                  component={InputField}
                                  required
                                  placeholder="Nom..."
                                />
                              </Columns.Column>
                              <Columns.Column size={6}>
                                <Field
                                  label="Prénom"
                                  name={`newUsers.${index}.firstName`}
                                  component={InputField}
                                  required
                                  placeholder="Prénom..."
                                />
                              </Columns.Column>
                              <Columns.Column size={6}>
                                <Field
                                  label="Matricule Interne"
                                  name={`newUsers.${index}.internalRef`}
                                  component={InputField}
                                  help={
                                    <>
                                      Uniquement visible par les collaborateurs de{' '}
                                      <b>{currentOrganization?.name}</b>.
                                      {!user && (
                                        <>
                                          <br />
                                          Un matricule sera généré automatiquement si ce champ n'est
                                          pas rempli.
                                        </>
                                      )}
                                    </>
                                  }
                                />
                              </Columns.Column>
                              <Columns.Column size={12}>
                                <Field
                                  label="Information Interne"
                                  name={`newUsers.${index}.internalInformation`}
                                  component={InputField}
                                  help={
                                    <>
                                      Uniquement visible par les collaborateurs de{' '}
                                      <b>{currentOrganization?.name}</b>.
                                    </>
                                  }
                                />
                              </Columns.Column>
                              <Columns.Column size={12}>
                                <Field
                                  label="Commentaire Interne"
                                  name={`newUsers.${index}.internalComment`}
                                  component={InputField}
                                  help={
                                    <>
                                      Uniquement visible par les collaborateurs de{' '}
                                      <b>{currentOrganization?.name}</b>.
                                    </>
                                  }
                                />
                              </Columns.Column>
                              {role === 'worker' &&
                                currentOrganization?.type === OrganizationTypeEnum.employer && (
                                  <Protected roles={['employerMember']}>
                                    <Columns.Column size={12}>
                                      <Element mt={5} data-test="contractual-schedules-section">
                                        <ContractualSchedulesSection
                                          name={`newUsers.${index}.contractualSchedules`}
                                          hasWorkerEverHadTimeSlots={hasWorkerEverHadTimeSlots(
                                            userAssociation,
                                          )}
                                        />
                                      </Element>
                                    </Columns.Column>
                                  </Protected>
                                )}
                              {user && (
                                <Protected roles={['interimAgencyMember']}>
                                  <>
                                    <Heading size={5} mb={1} mt={5}>
                                      Gestion / Délégation
                                    </Heading>
                                    <Element textSize={7} mb={4}>
                                      Si "gestion" est sélectionné pour le travailleur et
                                      l'Entreprises Utilisatrices, le coefficient gestion du métier
                                      sera pris en compte pour le calcul du côut horaire. Sinon, le
                                      coefficient délégation sera utilisé.
                                    </Element>
                                    {(associatedEmployerQuery.data || []).map(employer => (
                                      <Element key={employer._id}>
                                        <Field
                                          component={CheckboxField}
                                          label={employer.name}
                                          text={'Gestion'}
                                          name={`newUsers.${index}.laborCostCoefficients[${employer._id}].isManagement`}
                                        ></Field>
                                      </Element>
                                    ))}
                                  </>
                                </Protected>
                              )}
                            </Columns>
                          )}
                        </Element>
                      )
                    })
                  }}
                </FieldArray>
              )}
              <ComponentFooter>
                <ComponentFooter.Left>
                  <Protected roles={['interimAgencyMember', 'employerMember']}>
                    {!noAppAccess && !user && role === 'worker' && (
                      <Button
                        onClick={() => importCsvModal.setIsDisplayed(true)}
                        color="primary"
                        outlined
                        data-test="import-from-csv-action"
                      >
                        Import depuis un fichier CSV
                      </Button>
                    )}
                  </Protected>
                </ComponentFooter.Left>
                <ComponentFooter.Right>
                  <RequestButton
                    color="primary"
                    type="submit"
                    data-test="submit-invite-user"
                    mutation={
                      user
                        ? role === 'employerMember'
                          ? updateUserOrganizationRole
                          : updateMutation
                        : createMutation
                    }
                  >
                    {user
                      ? 'Mettre à jour'
                      : noAppAccess
                        ? 'Créer un utilisateur Offline'
                        : 'Inviter'}
                  </RequestButton>
                  <RequestMessage mutation={user ? updateMutation : createMutation} />
                </ComponentFooter.Right>
              </ComponentFooter>
            </Container>
          </Form>
        )
      }}
    </Formik>
  )
}

export default UserForm

const useDescription = (role: UserFormProps['role'], organizationType?: OrganizationTypeEnum) =>
  React.useMemo(() => {
    switch (role) {
      case 'worker':
        return (
          <>
            Invitez un {organizationType === OrganizationTypeEnum.employer ? 'nouveau' : 'nouvel'}{' '}
            <WorkerLabel lowercase /> à collaborer avec votre Agence.
            <br />
            <br />
            Il devra accepter votre invitation en se connectant :
            <Element className="content" mb={5}>
              <ul>
                <li>Soit via l'app mobile TeamTim (compatible Android et iOS)</li>
                <li>Soit via la plateforme web TeamTim</li>
              </ul>
            </Element>
            <Element mb={3}>
              S'il s'agit de la première fois qu'il utilise TeamTim, il devra compléter son profil
              avant d'accepter l'invitation.
            </Element>
          </>
        )
      case 'interimAgencyMember':
      case 'employerMember':
        return "Inviter un Nouvel Utilisateur. Cette personne pourra gérer l'agence au même titre que vous."
      default:
        return 'Inviter un Nouveau Super Administrateur de la plateforme. Cette personne aura les mêmes droits que vous.'
    }
  }, [role])
